import React, { FC, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { getCaseStatistics } from '../utils/functions';
import { UsersUrl } from '../utils/network';
import { getAuthToken } from '../utils/functions';
import { AuthTokenType } from '../utils/types';
import axios from 'axios';

// Color palette for discrimination basis (donut chart)
const BASIS_COLORS: Record<string, string> = {
  'Age': '#03AED2',               // Indigo
  'Color': '#F472B6',             // Pink
  'Mental/Physical Handicap': '#35858E', // Emerald
  'National Origin': '#FB923C',   // Orange
  'Race': '#A78BFA',              // Violet
  'Religion': '#A98B76',          // Blue
  'Reprisal': '#CA8A04',          // Amber
  'Sex': '#2DD4BF',               // Teal
  'Unknown': '#94A3B8',           // Slate
};

// Color palette for case type (bar chart)
const TYPE_COLORS: Record<string, string> = {
  'Nonselection': '#F87171',      // Red
  'Different Treatment': '#FB923C', // Orange
  'Accommodation': '#34D399',     // Emerald
  'Harassment': '#FB7185',        // Rose
  'Termination': '#60A5FA',       // Blue
  'Sexual Harassment': '#94A3B8', // Slate
  'Unknown': '#CBD5E1',           // Light Slate
};

interface DashboardStatsProps {
  currentUser: {
    fullname: string;
    role: string;
    email: string;
  };
}

const DashboardStats: FC<DashboardStatsProps> = ({ currentUser }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [userCount, setUserCount] = useState(0);
  const [activeUserCount, setActiveUserCount] = useState(0);
  const [pendingAlerts, setPendingAlerts] = useState(0);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsData] = await Promise.all([
        getCaseStatistics(),
      ]);
      setStats(statsData);

      // Fetch user data for system health
      try {
        const headers = getAuthToken() as AuthTokenType;
        const usersResponse = await axios.get(UsersUrl, headers);
        const users = usersResponse.data;
        setUserCount(users.length);
        setActiveUserCount(users.filter((u: any) => u.is_active).length);
        setPendingAlerts(users.filter((u: any) => !u.approved && u.approved !== undefined).length);
      } catch { /* ignore user fetch errors for non-admin */ }
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  // Prepare chart data
  const basisData = stats?.basis_statistics
    ? Object.entries(stats.basis_statistics).map(([key, val]: [string, any]) => ({
      name: key,
      value: val.total,
      color: BASIS_COLORS[key] || '#9CA3AF',
    }))
    : [];

  const typeData = stats?.type_statistics
    ? Object.entries(stats.type_statistics).map(([key, val]: [string, any]) => ({
      name: key,
      value: val.total,
      color: TYPE_COLORS[key] || '#9CA3AF',
    }))
    : [];

  const totalCases = stats?.total_cases || 0;
  const thisMonthCases = stats?.this_month_cases || 0;

  // Calculate "open" and "resolved" from likelihood
  const likelihoodStats = stats?.likelihood_statistics || {};
  const resolvedCases = (likelihoodStats['High']?.count || 0) + (likelihoodStats['Low']?.count || 0) + (likelihoodStats['Moderate']?.count || 0);
  const openCases = totalCases - resolvedCases;

  // Percentages relative to total cases
  const openPercent = totalCases > 0 ? Math.round((openCases / totalCases) * 100) : 0;
  const resolvedPercent = totalCases > 0 ? Math.round((resolvedCases / totalCases) * 100) : 0;
  const thisMonthPercent = totalCases > 0 ? Math.round((thisMonthCases / totalCases) * 100) : 0;

  // System health % (active users / total users)
  const systemHealth = userCount > 0 ? Math.round((activeUserCount / userCount) * 100) : 100;

  if (loading) {
    return (
      <div className="w-full px-4 md:px-[60px] pb-10">
        <div className="flex items-center justify-center py-16">
          <div className="w-8 h-8 border-3 border-[#4682B4] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="w-full px-4 md:px-[60px] pb-10 relative z-[5]">
      {/* Dashboard Title */}
      <div className="text-center mb-6 md:mb-8">
        <h2 className="font-['Lexend',sans-serif] text-[18px] md:text-[24px] lg:text-[32px] font-semibold text-[#1f2a37]">Dashboard</h2>
      </div>

      {/* Stat Cards Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5 mb-6 md:mb-8">
        {/* Total Cases */}
        <StatCard
          title="Total Cases"
          value={totalCases}
          subtitle="cases"
          bgColor="#EEF2FF"
          accentColor="#6366F1"
          iconBg="#E0E7FF"
          onClick={() => navigate('/cases')}
          icon={
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6366F1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14,2 14,8 20,8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
              <polyline points="10,9 9,9 8,9" />
            </svg>
          }
        />

        {/* Open Cases */}
        <StatCard
          title="Open Cases"
          value={openCases}
          subtitle={`${openPercent}%`}
          bgColor="#FFFBEB"
          accentColor="#F59E0B"
          iconBg="#FEF3C7"
          icon={
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
            </svg>
          }
        />

        {/* Resolved Cases */}
        <StatCard
          title="Resolved Cases"
          value={resolvedCases}
          subtitle={`${resolvedPercent}%`}
          bgColor="#ECFDF5"
          accentColor="#10B981"
          iconBg="#D1FAE5"
          icon={
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22,4 12,14.01 9,11.01" />
            </svg>
          }
        />

        {/* This Month */}
        <StatCard
          title="This Month"
          value={thisMonthCases}
          subtitle={`${thisMonthPercent}%`}
          bgColor="#EFF6FF"
          accentColor="#4682B4"
          iconBg="#DBEAFE"
          icon={
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4682B4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
          }
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 md:gap-6 mb-6 md:mb-8">
        {/* Cases by Discrimination Basis - Donut Chart */}
        <div className="premium-gradient-card p-5 md:p-7">

          <div className="mb-1">
            <h3 className="font-['Lexend',sans-serif] text-[14px] md:text-[16px] font-semibold text-[#1f2a37] m-0">Cases by Discrimination Basis</h3>
            <p className="font-['Lexend',sans-serif] text-[11px] md:text-[12px] font-light text-[#64748b] m-0 mt-1">Distribution by discrimination basis</p>
          </div>

          {basisData.length > 0 ? (
            <div className="flex flex-col items-center">
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={basisData}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={90}
                    paddingAngle={2}
                    dataKey="value"
                    stroke="none"
                  >
                    {basisData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      background: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: '10px',
                      fontFamily: 'Lexend, sans-serif',
                      fontSize: '12px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    }}
                    formatter={(value: any, name?: any) => [`${value} cases`, name || '']}
                  />
                </PieChart>
              </ResponsiveContainer>
              {/* Legend */}
              <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 mt-2">
                {basisData.map((entry, index) => (
                  <div key={index} className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: entry.color }} />
                    <span className="font-['Lexend',sans-serif] text-[10px] md:text-[11px] text-[#64748b] font-medium">{entry.name}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-[220px] text-[#94a3b8] font-['Lexend',sans-serif] text-[13px]">No data available</div>
          )}
        </div>

        {/* Cases by Case Type - Bar Chart */}
        <div className="premium-gradient-card p-5 md:p-7">

          <div className="mb-4">
            <h3 className="font-['Lexend',sans-serif] text-[14px] md:text-[16px] font-semibold text-[#1f2a37] m-0">Cases by Case Type</h3>
            <p className="font-['Lexend',sans-serif] text-[11px] md:text-[12px] font-light text-[#64748b] m-0 mt-1">Distribution by case type</p>
          </div>

          {typeData.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={typeData} barSize={32}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 10, fontFamily: 'Lexend, sans-serif', fill: '#64748b' }}
                  axisLine={{ stroke: '#e2e8f0' }}
                  tickLine={false}
                  interval={0}
                  angle={-20}
                  textAnchor="end"
                  height={60}
                />
                <YAxis
                  tick={{ fontSize: 11, fontFamily: 'Lexend, sans-serif', fill: '#94a3b8' }}
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                />
                <Tooltip
                  contentStyle={{
                    background: 'white',
                    border: '1px solid #e2e8f0',
                    borderRadius: '10px',
                    fontFamily: 'Lexend, sans-serif',
                    fontSize: '12px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                  }}
                  formatter={(value: any, name?: any) => [`${value} cases`, 'Count']}
                  cursor={{ fill: '#4682B410' }}
                />
                <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                  {typeData.map((entry, index) => (
                    <Cell key={`bar-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[240px] text-[#94a3b8] font-['Lexend',sans-serif] text-[13px]">No data available</div>
          )}
        </div>
      </div>

      {/* System Health Section */}
      <div className="premium-gradient-card p-5 md:p-7">

        <div className="mb-5">
          <div className="flex items-center gap-2">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4682B4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
              <line x1="8" y1="21" x2="16" y2="21" />
              <line x1="12" y1="17" x2="12" y2="21" />
            </svg>
            <h3 className="font-['Lexend',sans-serif] text-[14px] md:text-[16px] font-semibold text-[#1f2a37] m-0">System Health</h3>
          </div>
          <p className="font-['Lexend',sans-serif] text-[11px] md:text-[12px] font-light text-[#64748b] m-0 mt-1 ml-[26px]">User management and system status overview</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5">
          {/* Active Users */}
          <HealthCard
            icon={
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#4682B4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            }
            value={activeUserCount}
            label="Active Users"
            bgColor="#EFF6FF"
            onClick={() => navigate('/users')}
          />

          {/* Pending Topics */}
          <HealthCard
            icon={
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            }
            value={pendingAlerts}
            label="Pending Requests"
            bgColor="#FFFBEB"
          />

          {/* Total Alerts */}
          <HealthCard
            icon={
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
            }
            value={openCases}
            label="Open Alerts"
            bgColor="#FEF2F2"
          />

          {/* System Health */}
          <HealthCard
            icon={
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#4682B4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                <line x1="8" y1="21" x2="16" y2="21" />
                <line x1="12" y1="17" x2="12" y2="21" />
              </svg>
            }
            value={`${systemHealth}%`}
            label="System Health"
            bgColor="#EFF6FF"
            isPercentage
          />
        </div>
      </div>
    </div>
  );
};

/* ---- Reusable Sub-Components ---- */

interface StatCardProps {
  title: string;
  value: number;
  subtitle: string;
  bgColor: string;
  accentColor: string;
  iconBg: string;
  icon: React.ReactNode;
  onClick?: () => void;
}

const StatCard: FC<StatCardProps> = ({ title, value, subtitle, bgColor, accentColor, iconBg, icon, onClick }) => (
  <div
    className={`rounded-[14px] md:rounded-[17px] p-4 md:p-5 flex flex-col justify-between min-h-[100px] md:min-h-[120px] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0px_8px_20px_rgba(0,0,0,0.08)] ${onClick ? 'cursor-pointer' : ''}`}
    onClick={onClick}
    style={{
      backgroundColor: bgColor,
      boxShadow: '0px 4px 12px rgba(0,0,0,0.04)',
    }}
  >
    <div className="flex items-start justify-between mb-3">
      <span className="font-['Lexend',sans-serif] text-[11px] md:text-[13px] lg:text-[15px] font-semibold text-[#475569]">{title}</span>
      <div
        className="w-7 h-7 md:w-8 md:h-8 rounded-lg flex items-center justify-center shrink-0"
        style={{ backgroundColor: iconBg }}
      >
        {icon}
      </div>
    </div>
    <div>
      <span className="font-['DM_Sans',sans-serif] text-[24px] md:text-[32px] font-bold leading-none" style={{ color: accentColor }}>{value}</span>
      <span className="block font-['Lexend',sans-serif] text-[10px] md:text-[11px] font-medium text-[#94a3b8] mt-1">{subtitle}</span>
    </div>
  </div>
);

interface HealthCardProps {
  icon: React.ReactNode;
  value: number | string;
  label: string;
  bgColor: string;
  isPercentage?: boolean;
  onClick?: () => void;
}

const HealthCard: FC<HealthCardProps> = ({ icon, value, label, bgColor, isPercentage, onClick }) => (
  <div
    className={`rounded-[14px] md:rounded-[17px] px-4 py-5 md:px-6 md:py-6 flex flex-col items-center text-center transition-all duration-300 hover:-translate-y-0.5 ${onClick ? 'cursor-pointer' : ''}`}
    onClick={onClick}
    style={{
      backgroundColor: bgColor,
      boxShadow: '0px 2px 8px rgba(0,0,0,0.03)',
    }}
  >
    <div className="mb-3 opacity-70">{icon}</div>
    <span className={`font-['DM_Sans',sans-serif] font-bold leading-none mb-1.5 ${isPercentage ? 'text-[22px] md:text-[28px] text-[#4682B4]' : 'text-[20px] md:text-[26px] text-[#1f2a37]'}`}>
      {value}
    </span>
    <span className="font-['Lexend',sans-serif] text-[10px] md:text-[11px] font-medium text-[#94a3b8]">{label}</span>
  </div>
);

export default DashboardStats;